import { ref } from 'vue'
import { sendRequest } from 'best-modules/vue-utils'
import { minLength, required } from '@vuelidate/validators'
import { defineLocalStore } from '@/utils/localStore'
import { useVuelidate } from '@vuelidate/core'
import { RouteLocationNamedRaw } from 'vue-router'

type Tag = {
  value: string
  title: string
}

type ResultItem = {
  id: number
  title: string
  subtitle?: string
  type: string
  route: RouteLocationNamedRaw
  author: {
    id: number
    name: string
    surname: string
    patronymic: string
  }
}

export const {
  initLocalStore: initGlobalSearch,
  useLocalStore: useGlobalSearch,
} = defineLocalStore('globalSearch', () => {
  const resultList = ref<ResultItem[]>([])
  const isSearch = ref(false)
  const searchQuery = ref('')
  const selectedTags = ref<string[]>([])
  const placeholderSearch = ref('Пошук документів, завдань, контрагентів...')

  const searchTags = <Tag[]>[
    { value: 'order', title: 'Накази' },
    { value: 'protocol', title: 'Протоколи' },
    { value: 'documentAgreement', title: 'Погодження' },
    { value: 'documentSignature', title: 'Підписання' },
    { value: 'task', title: 'Завдання' },
    { value: 'contractor', title: 'Контрагент' },
  ]

  const rules = { selectedTags: { required, minLength: minLength(1) } }
  const v$ = useVuelidate(rules, { selectedTags })

  const toggleTag = (value: string) => {
    if (selectedTags.value.includes(value)) {
      selectedTags.value = selectedTags.value.filter(t => t !== value)
    } else {
      selectedTags.value.push(value)
    }
  }

  const removeTag = (value: string) => {
    selectedTags.value = selectedTags.value.filter(t => t !== value)
  }

  const onEnter = async () => {
    const params = new URLSearchParams()

    selectedTags.value.forEach(t => params.append('types[]', t))
    params.append('search', searchQuery.value.trim())

    await submit(params)
  }

  const formatResults = (data: Record<string, any[]>): ResultItem[] => {
    const unifiedItems: ResultItem[] = []

    for (const [type, items] of Object.entries(data)) {
      for (const item of items) {
        let formatted: ResultItem | null = null
        switch (type) {
          case 'order':
            formatted = {
              id: item.id,
              title: item.name,
              subtitle: item.number,
              author: item.author || 'Автор відсутній',
              type,
              route: {
                name: 'single-order',
                params: {
                  id: item.id,
                },
              },
            }
            break
          case 'protocol':
            formatted = {
              id: item.id,
              title: item.name,
              subtitle: item.number,
              author: item.author || 'Автор відсутній',
              type,
              route: {
                name: 'single-protocol',
                params: {
                  id: item.id,
                },
              },
            }
            break
          case 'documentAgreement':
            formatted = {
              id: item.id,
              title: item.name,
              subtitle: item.number,
              author: item.author || 'Автор відсутній',
              type,
              route: {
                name: 'document-agreement-single',
                params: {
                  id: item.id,
                },
              },
            }
            break
          case 'documentSignature':
            formatted = {
              id: item.id,
              title: item.name,
              subtitle: item.number,
              author: item.author || 'Автор відсутній',
              type,
              route: {
                name: 'document-signature-single',
                params: {
                  id: item.id,
                },
              },
            }
            break
          case 'task':
            formatted = {
              id: item.id,
              title: 'Завдання',
              subtitle: `Тема: ${item.topic || 'відсутня'}`,
              author: item.author || 'Автор відсутній',
              type,
              route: {
                name: 'single-task',
                params: {
                  id: item.id,
                },
              },
            }
            break
          case 'contractor':
            formatted = {
              id: item.id,
              title: item.shortName,
              subtitle: `ЄДРПОУ: ${item.edrpou || 'відсутній'}`,
              author: item.author || 'Автор відсутній',
              type,
              route: {
                name: 'contractor-single',
                params: {
                  id: item.id,
                },
              },
            }
            break
        }

        if (formatted) unifiedItems.push(formatted)
      }
    }

    return unifiedItems
  }

  const submit = (params: any) => {
    v$.value.$validate()
    if (!v$.value.$invalid) {
      isSearch.value = false
      return sendRequest({
        name: 'globalSearch',
        method: 'get',
        url: `/json/generalSearch?${params.toString()}`,
      }).then(result => {
        isSearch.value = true
        resultList.value.splice(0)
        resultList.value = formatResults(result)
      })
    }
  }

  return {
    placeholderSearch,
    searchTags,
    selectedTags,
    searchQuery,
    isSearch,
    removeTag,
    resultList,
    toggleTag,
    onEnter,
    submit,
    v$,
  }
})
